import { useEffect, useRef, useState } from 'react';

import { fetchSubmission, submitExercise } from './exerciseApi';
import type { SubmissionData } from './exerciseTypes';

export function useExerciseSubmission(exerciseId: string) {
  const [submission, setSubmission] = useState<SubmissionData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollingRef = useRef<number | null>(null);

  useEffect(() => {
    if (!submission?.id || submission.status === 'DONE') {
      return undefined;
    }

    const poll = async () => {
      try {
        const next = await fetchSubmission(submission.id as string);
        setSubmission((current) => ({ ...(current ?? {}), ...(next as SubmissionData) }));
        if ((next as SubmissionData).status !== 'DONE') {
          pollingRef.current = window.setTimeout(poll, 800);
        }
      } catch (pollError) {
        setError(pollError instanceof Error ? pollError.message : 'Unable to check submission status.');
      }
    };

    pollingRef.current = window.setTimeout(poll, 800);
    return () => {
      if (pollingRef.current) {
        window.clearTimeout(pollingRef.current);
      }
    };
  }, [submission]);

  const submitCommand = async (command: string) => {
    try {
      setIsSubmitting(true);
      setError(null);
      const response = await submitExercise(exerciseId, command);
      setSubmission(response as SubmissionData);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Unable to submit command.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitQuiz = async (selectedOptionIds: string[]) => {
    try {
      setIsSubmitting(true);
      setError(null);
      const response = await submitExercise(exerciseId, undefined, selectedOptionIds);
      setSubmission(response as SubmissionData);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Unable to submit answer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return { submission, isSubmitting, error, submitCommand, submitQuiz };
}
